import Dagre from "@dagrejs/dagre";
import type { Node, Edge } from "@xyflow/react";
import { Position } from "@xyflow/react";

import type { DeploymentEvent } from "./useDeploymentEvents";

// ── Types ─────────────────────────────────────────────────────────────────────

export type PipelineNodeType =
  | "vcs-badge" // Static VCS prerequisite badge (not a pipeline step)
  | "group-build" // Group node: all build services combined
  | "group-deploy" // Group node: all deploy services combined
  | "group-ci"; // Group node: all CI services combined (ci_managed mode)

export interface PipelineNodeEvent {
  eventType: string;
  status: string;
  message: string;
  timestamp: string;
}

export interface PipelineGroupService {
  name: string;
  type: string;
  status: string;
  message: string;
  shared?: boolean;
}

export interface PipelineNodeData {
  label: string;
  step: string;
  nodeType: PipelineNodeType;
  status: string;
  message: string;
  iconName: string;
  serviceName?: string;
  serviceType?: string;
  startedAt?: string | null;
  isStale: boolean;
  retryCount: number;
  deployModeBadge?: string;
  /** For group nodes: list of services with per-service status */
  services?: PipelineGroupService[];
  steps?: {
    name: string;
    status: string;
    message?: string;
    severity?: string;
  }[];
  /** Compact event summary for inline DAG display — last N events */
  recentEvents?: PipelineNodeEvent[];
  [key: string]: unknown;
}

// ── Aggregate status ─────────────────────────────────────────────────────────

/** Compute the aggregate status for a group of services. */
export function aggregateStatus(services: { status: string }[]): string {
  if (services.length === 0) return "pending";
  if (services.some((s) => s.status === "failed")) return "failed";
  if (services.some((s) => s.status === "in_progress")) return "in_progress";
  if (services.every((s) => s.status === "completed")) return "completed";
  return "pending";
}

/**
 * Merge the service-level aggregate with the phase-level row status.
 * The phase-level row (serviceName="") captures orchestrator-level failures
 * (e.g., image verification) that aren't surfaced by individual service rows.
 * Phase status takes precedence when it is worse than the service aggregate.
 *
 * When the service aggregate is "pending" (meaning no real per-service rows
 * exist — only a synthetic placeholder), the phase-level status is
 * authoritative regardless of rank, since the phase row is the only real
 * signal available.
 */
export function mergeWithPhaseStatus(
  serviceAgg: string,
  phaseStatus: string | null | undefined,
): string {
  if (!phaseStatus) return serviceAgg;
  // When per-service rows are absent (synthetic "pending" aggregate), the
  // phase-level row is the only real status — use it directly.
  if (serviceAgg === "pending" && phaseStatus !== "pending") {
    return phaseStatus;
  }
  const rank: Record<string, number> = {
    failed: 3,
    in_progress: 2,
    queued: 1.5,
    pending: 1,
    completed: 0,
  };
  const aggRank = rank[serviceAgg] ?? 0;
  const phaseRank = rank[phaseStatus] ?? 0;
  return phaseRank > aggRank ? phaseStatus : serviceAgg;
}

/** Pick the most relevant serviceName for node click delegation. */
export function pickActiveService(
  services: PipelineGroupService[],
): string | undefined {
  return (
    services.find((s) => s.status === "in_progress")?.name ??
    services.find((s) => s.status === "failed")?.name ??
    services.find((s) => s.status === "completed")?.name ??
    services[0]?.name
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

export function getServiceStatus(
  serviceName: string,
  step: string,
  events: DeploymentEvent[],
): { status: string; message: string; retryCount: number } {
  const svcEvents = events.filter(
    (e) =>
      e.step === step &&
      (e.metadata?.serviceName as string | undefined) === serviceName,
  );
  if (svcEvents.length === 0)
    return { status: "pending", message: "Waiting...", retryCount: 0 };
  const latest = svcEvents[0];
  return {
    status: latest.status,
    message: latest.message,
    retryCount: latest.retryCount,
  };
}

export function getLayoutedElements(nodes: Node[], edges: Edge[]): Node[] {
  if (!nodes || nodes.length === 0) return [];

  const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "LR", nodesep: 40, ranksep: 160 });

  nodes.forEach((node) => {
    const isVcsBadge = node.data?.nodeType === "vcs-badge";
    // Group nodes are taller depending on service count
    const svcCount =
      (node.data?.services as PipelineGroupService[] | undefined)?.length ?? 1;
    const width = isVcsBadge ? 210 : 220;
    const height = isVcsBadge ? 60 : Math.max(80, 48 + svcCount * 22);
    g.setNode(node.id, { width, height });
  });

  edges.forEach((edge) => g.setEdge(edge.source, edge.target));
  Dagre.layout(g);

  const PAD = 32;
  const laidOut = nodes.map((node) => ({
    ...node,
    position: { x: g.node(node.id)?.x ?? 0, y: g.node(node.id)?.y ?? 0 },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
  }));

  // Normalize so no node has negative coordinates
  const minX = Math.min(...laidOut.map((n) => n.position.x - 110));
  const minY = Math.min(
    ...laidOut.map((n) => {
      const svcCount =
        (n.data?.services as PipelineGroupService[] | undefined)?.length ?? 1;
      const h = Math.max(80, 48 + svcCount * 22);
      return n.position.y - h / 2;
    }),
  );
  const offsetX = minX < PAD ? PAD - minX : 0;
  const offsetY = minY < PAD ? PAD - minY : 0;
  if (offsetX > 0 || offsetY > 0) {
    for (const node of laidOut) {
      node.position = {
        x: node.position.x + offsetX,
        y: node.position.y + offsetY,
      };
    }
  }

  return laidOut;
}
